/**
 * DocSidebarHistorySection Component
 * 
 * Displays the history/timeline section of the document sidebar.
 * Handles data fetching and renders the history timeline.
 */

import { useMemo } from "react";
import { useLanguage } from "@/i18n/useLanguage";
import { getUITranslation } from "@/lib/views/labels/translations";
import { useDocChat } from "../api/useDocChat";
import { HistoryTimeline } from "./HistoryTimeline";

type DocSidebarHistorySectionProps = {
  doctype: string;
  name: string;
};

/**
 * DocSidebarHistoryTimeline Container Component
 * 
 * Handles data fetching for sidebar history timeline.
 * 
 * Responsibilities:
 * - Calls useDocChat hook
 * - Extracts items and pagination state from transformed data
 * - Passes everything to presentational HistoryTimeline component
 * 
 * Note: useDocChat uses select to transform infinite query data to { items, hasMore }.
 * We use both the transformed data and hasNextPage from the raw query.
 */
function DocSidebarHistoryTimeline({ doctype, name }: { doctype: string; name: string }) {
  const {
    data,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useDocChat(doctype, name);

  // useDocChat's select function already flattens pages and sorts items
  // data is transformed to { items, hasMore }
  const items = data?.items ?? [];
  const hasMore = data?.hasMore ?? false;

  return (
    <HistoryTimeline
      items={items}
      isLoading={isLoading && items.length === 0}
      isFetchingMore={isFetchingNextPage}
      hasMore={hasMore}
      onLoadMore={hasNextPage ? fetchNextPage : undefined}
    />
  );
}

export function DocSidebarHistorySection({ doctype, name }: DocSidebarHistorySectionProps) {
  const { language } = useLanguage();
  
  const uiLabels = useMemo(() => {
    const getUI = (key: string, fallback: string) => getUITranslation(key, language) || fallback;
    return {
      history: getUI('history', 'History'),
    };
  }, [language]);

  return (
    <div className="space-y-3 sm:space-y-4">
      <div className="flex items-center gap-2 text-[11px] font-medium text-muted-foreground/80 uppercase tracking-[0.5px]">
        <span>{uiLabels.history}</span>
      </div>
      <DocSidebarHistoryTimeline doctype={doctype} name={name} />
    </div>
  );
}
